import { useRef, useState } from 'react';
import { Pressable } from 'react-native';
import { DatePicker } from '@/common/components/DatePicker/DatePicker';
import {
  IDatePickerProps,
  TDatePickerRef,
} from '@/common/components/DatePicker/DatePicker.types';
import {
  FloatingField,
  IFloatingFieldProps,
  LabeledInput,
} from '@/common/components/Fields/FloatingField/FloatingField';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import { styles } from './DateField.styles';

export const DateField = ({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  disabled,
  minDate,
  maxDate,
  startDate,
  endDate,
  monthsShown,
  className,
  allowClear,
  onBlur,
}: IDateFieldProps) => {
  const [focused, setFocused] = useState(false);
  const calendarRef = useRef<TDatePickerRef | null>(null);

  const isDirty = value !== null;
  const icon =
    allowClear && isDirty ? (
      <Pressable
        onPress={() => {
          onChange(null);
        }}
      >
        <Icon icon="circle-xmark" />
      </Pressable>
    ) : (
      <Icon icon={['far', 'calendar']} color="neutral20" />
    );

  return (
    <FloatingField
      focused={focused}
      disabled={disabled}
      error={error}
      hint={hint}
      className={className}
      onPress={() => {
        calendarRef.current?.setOpen(true);
      }}
    >
      <LabeledInput
        label={label}
        active={focused || isDirty}
        customElement={
          <DatePicker
            id={id}
            ref={calendarRef}
            variant="single"
            value={value}
            onChange={onChange}
            disabled={disabled}
            onCalendarOpen={() => setFocused(true)}
            onCalendarClose={() => {
              setFocused(false);
              onBlur?.(id);
            }}
            minDate={minDate}
            maxDate={maxDate}
            startDate={startDate}
            endDate={endDate}
            monthsShown={monthsShown}
          />
        }
        inputProps={{
          id,
        }}
      />

      <Stack className={styles.icon}>
        {disabled ? <Icon icon="lock" color="neutral20" /> : icon}
      </Stack>
    </FloatingField>
  );
};

type FloatingFieldExtendable = Pick<
  IFloatingFieldProps,
  'hint' | 'error' | 'disabled'
>;

export interface IDateFieldProps
  extends Pick<IDatePickerProps, 'monthsShown'>,
    FloatingFieldExtendable {
  id: string;
  label: string;
  value: Date | null;
  minDate?: Date;
  maxDate?: Date;
  startDate?: Date | null;
  endDate?: Date | null;
  className?: string;
  allowClear?: boolean;
  onBlur?: (id: string) => void;
  onChange: (val: Date | null) => void;
}
